import { useState } from "react";
import api from "../login/interceptors";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { FaUserEdit } from "react-icons/fa";
import useFocusTrap from '../../hooks/useFocusTrap';

export default function ResumeViewer({ candidate, theme, fetchCandidates }) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: candidate?.first_name || "",
    last_name: candidate?.last_name || ""
  });
  const modalRef = useFocusTrap(showEditModal, () => setShowEditModal(false));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeModal = (e) => {
    e.stopPropagation();
    setShowEditModal(false);
    setError(null);
  };

  const submitNameChange = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const errors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsLoading(false);
      return;
    }

    setFormErrors({});

    try {
      await api.patch(`/api/candidates/update-name/${candidate.lead_id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name
      });
      fetchCandidates();
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating name:", error);
      setError(error.message || "Failed to update candidate name. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowEditModal(true)}
        className="absolute top-2 right-14 bg-black text-white rounded-full w-6 h-6 flex items-center justify-center"
        aria-label="Close pop-up"
        title="Edit Candidate Name"
      >
        <FaUserEdit className="h-4 w-4" />
      </button>

      {showEditModal && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70"
        >
          <div
            ref={modalRef}
            className="bg-white rounded-md w-full max-w-47rem overflow-hidden relative flex flex-col max-sm:mx-2 mt-[70px]"
            style={{ borderColor: theme.palette.secondary.v4 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div
              className="flex justify-between items-center px-5 py-4 border-b"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              <h3
                id="modal-title"
                className="font-semibold tracking-wide"
              >
                Edit Candidate Name
              </h3>
              <button
                onClick={closeModal}
                aria-label="Close pop-up"
                tabIndex={0}
                className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 sm:px-14 pt-8 pb-10">
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-3">
                  <label
                    htmlFor="first_name"
                    className="block font-bold"
                    style={{ color: theme.palette.text.main }}
                  >
                    First Name
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-md"
                    style={{
                      borderColor: formErrors.first_name ? 'red' : theme.palette.secondary.v4
                    }}
                  />
                  {formErrors.first_name && (
                    <p className="text-sm text-accent-red mt-1">{formErrors.first_name}</p>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <label
                    htmlFor="last_name"
                    className="block font-bold"
                    style={{ color: theme.palette.text.main }}
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-md"
                    style={{
                      borderColor: formErrors.last_name ? 'red' : theme.palette.secondary.v4
                    }}
                  />
                  {formErrors.last_name && (
                    <p className="text-sm text-accent-red mt-1">{formErrors.last_name}</p>
                  )}
                </div>
              </div>
            </div>

            <div
              className="px-5 py-3 border-t flex justify-between bg-white"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              <button
                onClick={closeModal}
                className="rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={submitNameChange}
                disabled={isLoading}
                className="rounded-md px-3 py-1 text-white"
                style={{ backgroundColor: theme.palette.info.main }}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
